import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import type { SnsBuzz } from '../../types/api.js';

interface SnsBuzzCardProps {
  snsBuzz: SnsBuzz;
}

/** @graph-connects none */
const PLATFORM_ICONS: Record<string, string> = {
  YouTube: '▶',
  'X (Twitter)': '𝕏',
  Instagram: '📷',
  'ブログ・レビュー': '📝',
};

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business SNSバズ度表示。YouTube/X/Instagram/ブログ別のヒット数とYouTube再生回数を可視化
 * @graph-connects none
 */
export function SnsBuzzCard({ snsBuzz }: SnsBuzzCardProps) {
  const { t } = useTranslation();

  const totalCount = snsBuzz.platforms.reduce((sum, p) => sum + p.count, 0);
  if (totalCount === 0) return null;

  const maxCount = Math.max(...snsBuzz.platforms.map((p) => p.count), 1);

  return (
    <Card variant="glass">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">
        {t('market.snsTitle')}
      </h3>

      <div className="space-y-3">
        {snsBuzz.platforms.map((platform) => (
          <div key={platform.name}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-[var(--color-text-secondary)]">
                {PLATFORM_ICONS[platform.name] ?? '●'} {platform.name}
              </span>
              <span className="text-sm font-mono text-[var(--color-text-primary)]">
                {platform.count} {t('market.hits')}
                {platform.views != null && platform.views > 0 && (
                  <span className="text-xs text-[var(--color-text-muted)] ml-1">
                    (
                    {platform.views >= 10000
                      ? `${(platform.views / 10000).toFixed(1)}${t('market.manViews')}`
                      : `${platform.views.toLocaleString()}${t('market.views')}`}
                    )
                  </span>
                )}
              </span>
            </div>
            <div className="score-bar">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${String(Math.round((platform.count / maxCount) * 100))}%`,
                  backgroundColor: 'var(--color-accent-primary)',
                  opacity: platform.count > 0 ? 1 : 0.2,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
